import { supabase } from './supabase';

function client() {
  if (!supabase) throw new Error('Cloud er ikke konfigureret.');
  return supabase;
}

export type RaccoonReportType = 'observed' | 'harvested';
export type RaccoonRange = '30d' | 'year' | 'all';

export interface RaccoonCell {
  cell_id: string;
  cell: { west: number; south: number; east: number; north: number };
  count_observed: number;
  count_harvested: number;
  latest_week: number | null;
  latest_year: number | null;
}

export interface MyRaccoonReport {
  id: string;
  report_type: RaccoonReportType;
  observed_on: string;
  photo_path: string | null;
  created_at: string;
}

const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function sinceDateFor(range: RaccoonRange, now: Date = new Date()): string | null {
  if (range === 'all') return null;
  if (range === 'year') return isoDate(new Date(now.getFullYear(), 0, 1));
  const d = new Date(now);
  d.setDate(d.getDate() - 30);
  return isoDate(d);
}

const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

// Teksten under kortet: den periode, som tallene dækker.
export function rangeLabel(range: RaccoonRange, now: Date = new Date()): string {
  const since = sinceDateFor(range, now);
  return since ? `Fra ${shortDate(since)} til ${shortDate(isoDate(now))}` : 'Alle datoer';
}

export function weekLabel(cell: Pick<RaccoonCell, 'latest_week' | 'latest_year'>): string | null {
  return cell.latest_week && cell.latest_year
    ? `Uge ${cell.latest_week}, ${cell.latest_year}`
    : null;
}

// Datoer der kan vælges ved indberetning: i dag og op til 14 dage tilbage (nyeste først).
export function selectableDates(now: Date = new Date()): string[] {
  return Array.from({ length: 15 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    return isoDate(d);
  });
}

export async function fetchRaccoonGrid(range: RaccoonRange): Promise<RaccoonCell[]> {
  const { data, error } = await client().rpc('get_raccoon_grid', {
    since_date: sinceDateFor(range),
  });
  if (error) throw new Error(error.message || 'Mårhundekortet kunne ikke hentes.');
  return ((data ?? []) as RaccoonCell[]).map((row) => ({
    ...row,
    count_observed: Number(row.count_observed),
    count_harvested: Number(row.count_harvested),
  }));
}

export async function submitRaccoonReport(input: {
  type: RaccoonReportType;
  lat: number;
  lng: number;
  observedOn: string;
  photo?: Blob | null;
}): Promise<void> {
  const c = client();
  const { data: sessionData } = await c.auth.getSession();
  const userId = sessionData.session?.user.id;
  if (!userId) throw new Error('Log ind for at indberette.');
  let photoPath: string | null = null;
  if (input.photo) {
    photoPath = `${userId}/${crypto.randomUUID()}.jpg`;
    const { error: uploadError } = await c.storage
      .from('raccoon-photos')
      .upload(photoPath, input.photo, { contentType: 'image/jpeg', upsert: false });
    if (uploadError) throw new Error('Billedet kunne ikke uploades.');
  }
  const { error } = await c.rpc('submit_raccoon_report', {
    report_type: input.type,
    lat: input.lat,
    lng: input.lng,
    observed_on: input.observedOn,
    photo_path: photoPath,
  });
  if (error) {
    if (photoPath) await c.storage.from('raccoon-photos').remove([photoPath]);
    throw new Error(error.message || 'Indberetningen kunne ikke gemmes.');
  }
}

export async function listMyRaccoonReports(): Promise<MyRaccoonReport[]> {
  const { data, error } = await client()
    .from('raccoon_reports')
    .select('id,report_type,observed_on,photo_path,created_at')
    .is('source', null)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message || 'Dine indberetninger kunne ikke hentes.');
  return (data ?? []) as MyRaccoonReport[];
}

export async function deleteMyRaccoonReport(report: MyRaccoonReport): Promise<void> {
  const c = client();
  const { error } = await c.from('raccoon_reports').delete().eq('id', report.id);
  if (error) throw new Error(error.message || 'Indberetningen kunne ikke slettes.');
  if (report.photo_path) await c.storage.from('raccoon-photos').remove([report.photo_path]);
}

export interface AdminRaccoonReport {
  id: string;
  email: string;
  report_type: RaccoonReportType;
  observed_on: string;
  has_photo: boolean;
  source: string | null;
  created_at: string;
}

export async function adminListRaccoonReports(): Promise<AdminRaccoonReport[]> {
  const { data, error } = await client().rpc('admin_list_raccoon_reports');
  if (error) throw new Error(error.message || 'Indberetningerne kunne ikke hentes.');
  return (data ?? []) as AdminRaccoonReport[];
}

export async function adminDeleteRaccoonReport(id: string): Promise<void> {
  const { error } = await client().rpc('admin_delete_raccoon_report', { report_id: id });
  if (error) throw new Error(error.message || 'Indberetningen kunne ikke slettes.');
}

export async function adminDeleteRaccoonSource(source: string): Promise<number> {
  const { data, error } = await client().rpc('admin_delete_raccoon_source', {
    source_name: source,
  });
  if (error) throw new Error(error.message || 'Kunne ikke slette importerede fund.');
  return Number(data ?? 0);
}

export async function adminListRaccoonCell(cellId: string): Promise<AdminRaccoonReport[]> {
  const { data, error } = await client().rpc('admin_list_raccoon_cell', { cell_key: cellId });
  if (error) throw new Error(error.message || 'Indberetningerne kunne ikke hentes.');
  return (data ?? []) as AdminRaccoonReport[];
}

export async function adminDeleteRaccoonCell(cellId: string): Promise<number> {
  const { data, error } = await client().rpc('admin_delete_raccoon_cell', { cell_key: cellId });
  if (error) throw new Error(error.message || 'Området kunne ikke ryddes.');
  return Number(data ?? 0);
}
